import {AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FileDTO} from '../../../../common/models/FileDTO';
import {CreateNameValueCodeDTO, NameValueCodeDTO} from '../../../../common/models/NameValueCodeDTO';
import {LookupValue} from '../../../types/lookup-value.enum';
import {take, takeUntil} from 'rxjs/operators';
import {LookupService} from '../../../../common/services/lookup.service';
import {Subject} from 'rxjs';
import {SharedService} from '../../../../common/services/shared-service';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {DocumentReviewDTO} from '../../../models/claim-review/DocumentReviewDTO';
import {NameValueDTO} from '../../../../common/models/NameValueDTO';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator} from '@angular/material/paginator';
import {FileService} from '../../../../common/services/file.service';
import {FileUploadService} from '../../../../common/services/file-upload.service';
import {saveAs} from 'file-saver';
import {MatSort} from '@angular/material/sort';

const REFERENCE_TYPE_CODE_FID = 'FID';
const REFERENCE_TYPE_ID_FID = 1;

@Component({
  selector: 'greydom-add-document-dialog',
  templateUrl: './add-document-dialog.component.html',
  styleUrls: ['../../../../../../styles/global/_dialog-styles.scss']
})
export class AddDocumentDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  readonly destroyed$ = new Subject();
  fileUploadConfig: any = {
    uploadedFileData: null,
    clearFileData: true
  };
  selectedDocumentType: NameValueCodeDTO;
  documentTypes: NameValueCodeDTO[] = [];
  transactionId: number;

  displayedColumns: string[] = ['documentType', 'fileName', 'delete'];
  documentDataSource = new MatTableDataSource<DocumentReviewDTO>();

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    private sharedService: SharedService,
    private lookupService: LookupService,
    private fileService: FileService,
    private fileUploadService: FileUploadService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<AddDocumentDialogComponent>
  ) {
    this.documentDataSource.data = data.documents;
    this.transactionId = data.transactionId;
  }

  ngOnInit(): void {
    this.getListOfLookupValues(LookupValue.DocumentType, 'documentTypes');
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  ngAfterViewInit() {
    this.documentDataSource.paginator = this.paginator;
    this.documentDataSource.sort = this.sort;
    this.documentDataSource.sortingDataAccessor = (item, property) => {
      if (property === 'documentType') {
        return item.documentType?.name?.toLowerCase() || ''; // Define sorting for complex column documentType
      }
      return (item as any)[property]; // Default sorting for other columns
    };
    this.sort.active = 'documentType';
    this.sort.direction = 'asc';
    this.sort.sortChange.emit();
  }

  isDataAvailable(): boolean {
    return this.documentDataSource.data?.length > 0;
  }

  onFileUploadSuccess(uploadedFileResponse: any) {
    if (!uploadedFileResponse) {
      return;
    }
    const file: FileDTO = uploadedFileResponse;

    const document = new DocumentReviewDTO();
    document.transactionId = this.transactionId;
    document.fileName = file.name + file.extension;
    document.fileId = file.fileId;
    document.isActive = 1;
    document.documentType = this.selectedDocumentType;
    document.documentReferenceType = CreateNameValueCodeDTO(REFERENCE_TYPE_ID_FID, '', REFERENCE_TYPE_CODE_FID);
    this.documentDataSource.data = [...this.documentDataSource.data, {...document}];
  }

  onDocumentTypeChange(event: NameValueDTO) {
    this.selectedDocumentType = this.documentTypes.find(document => document.value == event.value);
  }

  onClickDelete(document: DocumentReviewDTO) {
    this.documentDataSource.data = this.documentDataSource.data.filter(doc => doc !== document);
  }

  onClickDownload(document: DocumentReviewDTO) {
    if (document.documentReferenceType.code != REFERENCE_TYPE_CODE_FID) {
      // TODO: handle other file reference types like URLs
      return;
    }
    this.fileService.getFile(document.fileId)
      .pipe(
          take(1)
        )
        .subscribe({
          next: (fileDTO: FileDTO) => {
            if (fileDTO) {
              const {name, extension, file} = fileDTO;
              const blobData = this.fileUploadService.base64toBlob(file, 'data:application/octet-stream;base64');
              const blob = new Blob([blobData]);
              saveAs(blob, `${name.trim()}${extension}`);
            }
          },
          error: (err) => this.sharedService.openErrorDialog(err.businessMessage)
        });
  }

  onClickCancel(): void {
    this.dialogRef.close();
  }

  onClickSave(): void {
    this.dialogRef.close(this.documentDataSource.data);
  }

  private getListOfLookupValues(entity: string, propertyName: string): void {
    this.lookupService.getListOfLookupValuesWithCode(entity)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response: NameValueCodeDTO[]) => {
          this[propertyName] = response;
          this.setDefaultValues(response, entity);
        },
        error: (error) => {
          this.sharedService.openErrorDialog(error.message);
        }
      });
  }

  private setDefaultValues(response: NameValueCodeDTO[], entity: string) {
    if (response == null || response.length < 1) {
      return;
    }
    if (entity == LookupValue.DocumentType) {
      this.selectedDocumentType = response[0];
    }
  }
}
